import { Box, Heading, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableIdGroupWithLoanProductGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  useGetFundingRequestVerificationReceivableEligibilityQuery,
  useGetVerificationBorrowingBaseWaiverImpactQuery,
} from '../requests/requests.gql.generated';
import { extractDataTapeMapFromRequiredDataTapes } from './extractDataTapeMapFromRequiredDataTapes';
import { uploadTapeForLenderVerification } from './uploadTapeForLenderVerification';
import { isVerificationBorrowingBaseWaiverImpactData } from './utils';
import { VerificationReceivableEligibilityTableContainer } from './VerificationReceivableEligibilityTable';
import {
  VerificationPipelineRunId,
  useVerificationOptions,
} from './VerificationTableSharedComponents';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useGetReceivableWaiversForCreditFacilityWithOrganizationQuery } from '~/packages/borrowing-base/requests/borrowingBase.gql.generated';
import {
  BreadcrumbProps,
  Breadcrumbs,
} from '~/packages/common-frontend/components/Breadcrumbs';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetRequiredDataTapesForVerificationQuery } from '~/packages/manualDataTapeUploads/requests/requests.gql.generated';
import { DataTape } from '~/packages/manualDataTapeUploads/types';

// @dani verification refactr to have immutable view when historical
type VerificationReceivableEligibilityContainerProps = {
  verificationReportId: string;
  pipelineRunId: VerificationPipelineRunId;
};
export const VerificationReceivableEligibilityContainer = ({
  verificationReportId,
  pipelineRunId,
}: VerificationReceivableEligibilityContainerProps) => {
  const { accessToken } = useUserAuthOrThrow();
  const navigate = useNavigate();
  const toast = useToast();
  const { navigator } = useVerificationOptions();

  const { artifactDocumentId, creditFacilityId, fileVersion } = pipelineRunId;

  const {
    data: receivableEligibilityData,
    loading: receivableEligibilityLoading,
  } = useGetFundingRequestVerificationReceivableEligibilityQuery({
    variables: { creditFacilityId, verificationReportId },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  const {
    data: receivableWaiversData,
    loading: isReceivableWaiversLoading,
  } = useGetReceivableWaiversForCreditFacilityWithOrganizationQuery({
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const {
    data: waiverImpactData,
    loading: isWaiverImpactLoading,
  } = useGetVerificationBorrowingBaseWaiverImpactQuery({
    variables: {
      verificationReportId,
      creditFacilityId,
    },
  });

  const [
    minimumReportingDate,
    setMinimumReportingDate,
  ] = useState<Util.FullDateStr | null>(null);
  const [dataTapes, setDataTapes] = useState<DataTape[]>([]);

  // mak-todo: remove this call.
  const {
    data: requiredDataTapesData,
  } = useGetRequiredDataTapesForVerificationQuery({
    variables: {
      creditFacilityId,
    },
    onCompleted(data) {
      const {
        dataTapes: extractedDataTapes,
        reportingDate,
      } = extractDataTapeMapFromRequiredDataTapes(data);

      setMinimumReportingDate(reportingDate);
      setDataTapes(extractedDataTapes);
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (!minimumReportingDate || !requiredDataTapesData) {
    return (
      <Skeleton data-testid="loading-verification-uploader" height="600px" />
    );
  }
  const upload = uploadTapeForLenderVerification({
    creditFacilityId,
    selectedReportingDate: minimumReportingDate,
    authToken: accessToken.raw,
  });

  if (
    !receivableEligibilityData ||
    !receivableEligibilityData.fundingRequestVerificationReceivableEligibility ||
    receivableEligibilityLoading ||
    !creditAgreementData ||
    isCreditAgreementLoading ||
    !receivableWaiversData ||
    isReceivableWaiversLoading ||
    !waiverImpactData ||
    isWaiverImpactLoading
  ) {
    return (
      <Skeleton height="600px" data-testid="receivable-eligibilitys-loading" />
    );
  }

  const { currentCreditAgreementFullArtifact } = creditAgreementData;

  let parsedCreditAgreement = null;
  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(
      currentCreditAgreementFullArtifact?.creditAgreement,
    );
  } catch (err) {
    Sentry.captureException(err);
    const errorToastTitle = `Error parsing current credit agreement artifact for credit facility ${creditFacilityId}`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    navigator(navigate).reset();
  }

  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for credit facility ${creditFacilityId}`,
  );

  const {
    reportingDate,
    verificationEligibilityReceivables,
  } = receivableEligibilityData.fundingRequestVerificationReceivableEligibility;

  const loanProducts = parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.map(
    (loanProductMechanic) => loanProductMechanic.entity,
  );

  if (!waiverImpactData.verificationBorrowingBaseWaiverImpact) {
    return <></>;
  }

  const borrowingBaseWaiverImpact = JSON.parse(
    waiverImpactData.verificationBorrowingBaseWaiverImpact,
  );

  if (!isVerificationBorrowingBaseWaiverImpactData(borrowingBaseWaiverImpact)) {
    return <></>;
  }

  const recalculate = async () => {
    // re-upload previous files
    // here we pass file: null to indicate that
    // uploader should use previous file.
    await Promise.all(
      dataTapes.map((dataTape) =>
        upload({ artifactId: dataTape.artifactId, file: null }),
      ),
    );
  };

  const breadcrumbs: BreadcrumbProps[] = [
    {
      label: 'Funding Request',
      path: '/funding-requests/',
    },
    {
      label: 'Verification',
      path: `/funding-requests/verifications/pipeline/${artifactDocumentId}/${creditFacilityId}/${reportingDate}/${fileVersion}`,
    },
    { label: 'Receivable Eligibility Differences' },
  ];

  const receivableWaiversByLoanProduct: {
    [loanProductId: string]: ReceivableBorrowingBaseTestWaiverGQLType[];
  } = _.groupBy(
    receivableWaiversData.receivableWaiversForCreditFacilityWithOrganization,
    (waiver) => waiver.loanProductId,
  );

  const receivableIdGroupsByLoanProduct: {
    [loanProductId: string]: ReceivableIdGroupWithLoanProductGQLType[];
  } = _.groupBy(
    borrowingBaseWaiverImpact.receivableResponseTapeReceivableIdGroups,
    (idGroup) => idGroup.loanProductId,
  );

  return (
    <Box>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box height="16px" />
      <Box>
        <Heading as="h2">Receivable Eligibility Differences</Heading>
      </Box>
      <Box h="32px" />
      <VerificationReceivableEligibilityTableContainer
        recalculate={recalculate}
        reportingDate={reportingDate}
        receivableEligibility={verificationEligibilityReceivables}
        creditAgreement={parsedCreditAgreement}
        creditFacilityId={creditFacilityId}
        loanProducts={loanProducts}
        receivableIdGroupsByLoanProduct={receivableIdGroupsByLoanProduct}
        receivableWaiversByLoanProduct={receivableWaiversByLoanProduct}
      />
    </Box>
  );
};
